/**
 * CMR (Continuous Medical Record) Mock Service
 * 
 * Provides mock patient data for testing without Firebase.
 * Simulates patient demographics, medical history, discharge plans,
 * and the SecureTransfer flow for patient-to-provider data sharing.
 * 
 * Usage:
 * ```javascript
 * import { mockCMRService, SAMPLE_PATIENTS, secureTransferMock } from 'services/mocks/cmrMocks';
 * 
 * // Patient side: Generate a shareable token
 * const token = await secureTransferMock.generateToken('patient-001');
 * 
 * // Provider side: Scan/validate token and get patient data
 * const patient = await mockCMRService.getPatientByQR(token.qrData);
 * ```
 */

import { MockServiceBase, MockDataStore, createMockResponse } from './index';

// ============================================================================
// SAMPLE PATIENT DATA
// ============================================================================

export const SAMPLE_PATIENTS = {
  'patient-001': {
    id: 'patient-001',
    patientId: 'P-2024-001',
    name: 'John Smith',
    firstName: 'John',
    lastName: 'Smith',
    dob: '1985-03-15',
    dateOfBirth: '1985-03-15',
    age: 39,
    sex: 'male',
    gender: 'Male',
    phone: '+1 555-123-4567',
    email: 'john.smith@email.com',
    address: {
      street: '123 Main Street',
      city: 'Springfield',
      state: 'IL',
      zip: '62701',
    },
    insuranceProvider: 'Blue Cross Blue Shield',
    insuranceId: 'BCBS-123456789',
    primaryCarePhysician: 'Dr. Sarah Johnson',
    emergencyContact: {
      name: 'Jane Smith',
      relationship: 'Spouse',
      phone: '+1 555-123-4568',
    },
    // PMH - Past Medical History (structured)
    pmh: [
      { condition: 'Hypertension', diagnosedYear: 2015, status: 'active', notes: 'Well controlled on Lisinopril' },
      { condition: 'Type 2 Diabetes', diagnosedYear: 2018, status: 'active', notes: 'A1C 7.2%' },
      { condition: 'Hyperlipidemia', diagnosedYear: 2016, status: 'controlled', notes: 'On statin therapy' },
    ],
    // PSH - Past Surgical History (structured)
    psh: [
      { procedure: 'Appendectomy', year: 2005, hospital: 'Springfield General', surgeon: 'Dr. Miller', notes: 'Uncomplicated' },
      { procedure: 'Right knee arthroscopy', year: 2019, hospital: 'Orthopedic Surgery Center', notes: 'Meniscus repair' },
    ],
    // Allergies (structured with severity)
    allergies: [
      { allergen: 'Penicillin', type: 'drug', reaction: 'Anaphylaxis', severity: 'severe' },
      { allergen: 'Sulfa drugs', type: 'drug', reaction: 'Rash', severity: 'moderate' },
    ],
    // Medications (enhanced)
    medications: [
      { name: 'Lisinopril', dosage: '10mg', frequency: 'Daily', reason: 'Hypertension', isHighRisk: false },
      { name: 'Aspirin', dosage: '81mg', frequency: 'Daily', reason: 'Cardiac prophylaxis', isHighRisk: false },
      { name: 'Metformin', dosage: '500mg', frequency: 'Twice daily', reason: 'Type 2 Diabetes', isHighRisk: false },
    ],
    // Other - Social/Family History
    other: {
      smokingStatus: 'Former smoker, quit 2010 (10 pack-year history)',
      alcoholUse: '1-2 drinks per week',
      drugUse: 'None',
      occupation: 'Accountant',
      socialHistory: 'Lives with spouse, works full-time, exercises 3x/week',
      familyHistory: 'Father: MI at age 55, Mother: Type 2 DM, Brother: Hypertension',
      additionalNotes: '',
    },
    conditions: ['Hypertension', 'Type 2 Diabetes', 'Hyperlipidemia'],
    recentVisits: [
      { date: '2024-01-15', provider: 'Dr. Johnson', reason: 'Annual physical' },
      { date: '2023-11-03', provider: 'Dr. Chen', reason: 'Chest pain evaluation' },
    ],
  },
  
  'patient-002': {
    id: 'patient-002',
    patientId: 'P-2024-002',
    name: 'Maria Garcia',
    firstName: 'Maria',
    lastName: 'Garcia',
    dob: '1992-07-22',
    dateOfBirth: '1992-07-22',
    age: 32,
    sex: 'female',
    gender: 'Female',
    phone: '+1 555-234-5678',
    email: 'maria.garcia@email.com',
    address: {
      street: '456 Oak Avenue',
      city: 'Springfield',
      state: 'IL',
      zip: '62702',
    },
    insuranceProvider: 'Aetna',
    insuranceId: 'AET-987654321',
    primaryCarePhysician: 'Dr. Michael Brown',
    emergencyContact: {
      name: 'Carlos Garcia',
      relationship: 'Brother',
      phone: '+1 555-234-5679',
    },
    // PMH
    pmh: [
      { condition: 'Generalized Anxiety Disorder', diagnosedYear: 2018, status: 'active', notes: 'On SSRI therapy' },
      { condition: 'Migraine with aura', diagnosedYear: 2015, status: 'active', notes: '2-3 episodes per month' },
    ],
    // PSH
    psh: [],
    // Allergies
    allergies: [
      { allergen: 'Latex', type: 'environmental', reaction: 'Contact dermatitis', severity: 'moderate' },
    ],
    // Medications
    medications: [
      { name: 'Sertraline', dosage: '50mg', frequency: 'Daily', reason: 'Anxiety', isHighRisk: false },
      { name: 'Sumatriptan', dosage: '50mg', frequency: 'PRN for migraine', reason: 'Migraine', isHighRisk: false },
    ],
    // Other
    other: {
      smokingStatus: 'Never smoker',
      alcoholUse: 'Occasional (social)',
      drugUse: 'None',
      occupation: 'Marketing manager',
      socialHistory: 'Single, lives alone, regular yoga practice',
      familyHistory: 'Mother: Migraines, Grandmother: Breast cancer (age 65)',
      additionalNotes: 'Prefers evening appointments',
    },
    conditions: ['Anxiety', 'Migraines'],
    recentVisits: [
      { date: '2024-02-10', provider: 'Dr. Brown', reason: 'Migraine follow-up' },
    ],
  },
  
  'patient-003': {
    id: 'patient-003',
    patientId: 'P-2024-003',
    name: 'Robert Johnson',
    firstName: 'Robert',
    lastName: 'Johnson',
    dob: '1958-11-08',
    dateOfBirth: '1958-11-08',
    age: 66,
    sex: 'male',
    gender: 'Male',
    phone: '+1 555-345-6789',
    email: 'robert.johnson@email.com',
    address: {
      street: '789 Pine Road',
      city: 'Springfield',
      state: 'IL',
      zip: '62703',
    },
    insuranceProvider: 'Medicare',
    insuranceId: 'MCR-111222333A',
    primaryCarePhysician: 'Dr. Emily White',
    emergencyContact: {
      name: 'Susan Johnson',
      relationship: 'Wife',
      phone: '+1 555-345-6790',
    },
    // PMH - Complex cardiac patient
    pmh: [
      { condition: 'Coronary Artery Disease', diagnosedYear: 2015, status: 'active', notes: '3-vessel disease, status post PCI 2020' },
      { condition: 'STEMI (Myocardial Infarction)', diagnosedYear: 2020, status: 'resolved', notes: 'Inferior wall MI, EF 45%' },
      { condition: 'GERD', diagnosedYear: 2012, status: 'controlled', notes: 'On PPI therapy' },
      { condition: 'Osteoarthritis', diagnosedYear: 2018, status: 'active', notes: 'Bilateral knees, right worse than left' },
      { condition: 'Hyperlipidemia', diagnosedYear: 2010, status: 'controlled', notes: 'LDL at goal on statin' },
    ],
    // PSH - Cardiac history
    psh: [
      { procedure: 'Percutaneous Coronary Intervention (PCI)', year: 2020, hospital: 'Springfield Heart Center', surgeon: 'Dr. Williams', notes: '2 drug-eluting stents to RCA' },
      { procedure: 'Colonoscopy', year: 2022, hospital: 'Springfield General', notes: 'Surveillance, benign polyp removed' },
    ],
    // Allergies - IMPORTANT for contrast
    allergies: [
      { allergen: 'Codeine', type: 'drug', reaction: 'Severe nausea/vomiting', severity: 'moderate' },
      { allergen: 'Iodine contrast', type: 'drug', reaction: 'Anaphylactoid reaction', severity: 'severe' },
    ],
    // Medications - Cardiac regimen
    medications: [
      { name: 'Atorvastatin', dosage: '40mg', frequency: 'Daily at bedtime', reason: 'Hyperlipidemia/CAD', isHighRisk: false },
      { name: 'Metoprolol succinate', dosage: '50mg', frequency: 'Twice daily', reason: 'CAD/Post-MI', isHighRisk: true },
      { name: 'Clopidogrel', dosage: '75mg', frequency: 'Daily', reason: 'Post-stent antiplatelet', isHighRisk: true },
      { name: 'Aspirin', dosage: '81mg', frequency: 'Daily', reason: 'CAD', isHighRisk: false },
      { name: 'Omeprazole', dosage: '20mg', frequency: 'Daily before breakfast', reason: 'GERD/GI protection on DAPT', isHighRisk: false },
    ],
    // Other - Detailed cardiac social history
    other: {
      smokingStatus: 'Former smoker, quit 2015 after MI (30 pack-year history)',
      alcoholUse: 'None since 2020',
      drugUse: 'None',
      occupation: 'Retired school principal',
      socialHistory: 'Lives with wife, independent ADLs, walks 30 min daily in cardiac rehab program',
      familyHistory: 'Father: Fatal MI at age 58, Mother: Stroke at age 72, Sister: Hypertension',
      additionalNotes: 'Participates in cardiac rehab. Due for cardiology f/u in 3 months. Premedication required before any contrast imaging.',
    },
    conditions: ['Coronary Artery Disease', 'Previous MI (2020)', 'GERD', 'Osteoarthritis'],
    recentVisits: [
      { date: '2024-01-20', provider: 'Dr. White', reason: 'Cardiac follow-up' },
      { date: '2023-12-01', provider: 'Cardiology', reason: 'Stress test' },
      { date: '2023-10-15', provider: 'Orthopedics', reason: 'Knee pain evaluation' },
    ],
  },
  
  'patient-004': {
    id: 'patient-004',
    patientId: 'P-2024-004',
    name: 'Emily Chen',
    firstName: 'Emily',
    lastName: 'Chen',
    dob: '2015-04-12',
    dateOfBirth: '2015-04-12',
    age: 9,
    sex: 'female',
    gender: 'Female',
    phone: '+1 555-456-7890',
    email: 'chen.family@email.com',
    address: {
      street: '321 Maple Drive',
      city: 'Springfield',
      state: 'IL',
      zip: '62704',
    },
    insuranceProvider: 'United Healthcare',
    insuranceId: 'UHC-444555666',
    primaryCarePhysician: 'Dr. Lisa Park (Pediatrics)',
    emergencyContact: {
      name: 'David Chen',
      relationship: 'Father',
      phone: '+1 555-456-7891',
    },
    // PMH - Pediatric
    pmh: [
      { condition: 'Asthma (mild intermittent)', diagnosedYear: 2020, status: 'active', notes: 'Uses PRN albuterol' },
    ],
    // PSH - None
    psh: [],
    // Allergies - Drug allergy
    allergies: [
      { allergen: 'Amoxicillin', type: 'drug', reaction: 'Hives', severity: 'moderate' },
    ],
    // Medications - PRN only
    medications: [
      { name: 'Albuterol HFA', dosage: '90mcg', frequency: 'PRN for wheezing', reason: 'Asthma', isHighRisk: false },
    ],
    // Other - Pediatric info
    other: {
      smokingStatus: 'N/A (pediatric)',
      alcoholUse: 'N/A (pediatric)',
      drugUse: 'N/A (pediatric)',
      occupation: '4th grade student',
      socialHistory: 'Lives with both parents and younger sibling. Active in soccer and swimming. Up to date on immunizations.',
      familyHistory: 'Mother: Seasonal allergies, Father: Childhood asthma (resolved)',
      additionalNotes: 'Immunizations current per CDC schedule. Last flu vaccine 10/2023.',
    },
    conditions: ['Asthma (mild intermittent)'],
    recentVisits: [
      { date: '2024-02-05', provider: 'Dr. Park', reason: 'Well child visit' },
      { date: '2023-09-15', provider: 'Urgent Care', reason: 'Ear infection' },
    ],
  },
};

// Sample discharge plans
export const SAMPLE_DISCHARGE_PLANS = {
  'discharge-001': {
    id: 'discharge-001',
    patientId: 'patient-001',
    encounterId: 'encounter-chest-001',
    createdAt: new Date('2024-02-15T14:30:00Z'),
    diagnosis: 'Unstable Angina',
    provider: 'Dr. James Wilson',
    medications: [
      { name: 'Aspirin 325mg', instructions: 'Take one tablet daily' },
      { name: 'Clopidogrel 75mg', instructions: 'Take one tablet daily' },
      { name: 'Nitroglycerin 0.4mg SL', instructions: 'Take as needed for chest pain' },
    ],
    followUpInstructions: 'Follow up with Cardiology within 1 week. Schedule cardiac catheterization.',
    activityRestrictions: 'No strenuous activity. Light walking OK. No driving for 48 hours.',
    dietInstructions: 'Heart-healthy diet. Limit sodium to 2g/day. Avoid fatty foods.',
    warningSignsToWatch: [
      'Chest pain lasting more than 15 minutes',
      'Shortness of breath at rest',
      'Dizziness or fainting',
      'Severe fatigue',
    ],
    returnPrecautions: 'Return to ED immediately if chest pain does not improve with nitroglycerin or if you experience any warning signs.',
  },
  
  'discharge-002': {
    patientId: 'patient-002',
    encounterId: 'encounter-headache-001',
    createdAt: new Date('2024-02-10T10:15:00Z'),
    diagnosis: 'Migraine with aura',
    provider: 'Dr. Amanda Lee',
    medications: [
      { name: 'Sumatriptan 50mg', instructions: 'Take at onset of migraine. May repeat in 2 hours if needed. Max 200mg/day.' },
      { name: 'Ibuprofen 400mg', instructions: 'Take with food as needed for pain' },
    ],
    followUpInstructions: 'Follow up with Neurology in 2-4 weeks for migraine management.',
    activityRestrictions: 'Rest in dark, quiet room during migraine episodes.',
    dietInstructions: 'Keep a headache diary. Avoid known triggers (caffeine, alcohol, aged cheeses).',
    warningSignsToWatch: [
      'Worst headache of your life',
      'Headache with fever and stiff neck',
      'Numbness or weakness that doesn\'t resolve',
      'Vision changes that persist',
    ],
    returnPrecautions: 'Return to ED if headache is severe and different from usual migraines, or if you develop neurological symptoms.',
  },
};

// ============================================================================
// CMR DATA STORE
// ============================================================================

const cmrDataStore = new MockDataStore({
  patients: { ...SAMPLE_PATIENTS },
  dischargePlans: { ...SAMPLE_DISCHARGE_PLANS },
  accessTokens: {},
});

// ============================================================================
// MOCK CMR SERVICE
// ============================================================================

class MockCMRService extends MockServiceBase {
  constructor() {
    super('MockCMRService');
  }
  
  /**
   * Get patient by QR code (simulates scanning patient's CMR QR)
   * @param {string} qrData - QR code data (in mock, can be patient ID)
   * @returns {Promise<Object>} Patient data
   */
  async getPatientByQR(qrData) {
    return this.wrapMethod('getPatientByQR', async () => {
      // In mock mode, QR data can be the patient ID directly
      const patientId = qrData.replace('cmr://', '').replace('patient-', '');
      const fullId = `patient-${patientId.padStart(3, '0')}`;
      
      const patient = cmrDataStore.get('patients', fullId);
      
      if (!patient) {
        // Generate a random mock patient if not found
        return createMockResponse({
          patientId: `P-${Date.now()}`,
          name: 'Mock Patient',
          dob: '1990-01-01',
          source: 'qr_scan',
          confidence: 'high',
        });
      }
      
      return createMockResponse({
        ...patient,
        source: 'qr_scan',
        confidence: 'high',
      });
    })();
  }
  
  /**
   * Get patient by ID scan (OCR extraction)
   * @param {string} base64Image - Base64 encoded image (ignored in mock)
   * @returns {Promise<Object>} Extracted patient data
   */
  async extractPatientFromID(base64Image) {
    return this.wrapMethod('extractPatientFromID', async () => {
      // Simulate OCR extraction with mock data
      const mockPatient = SAMPLE_PATIENTS['patient-002'];
      
      return createMockResponse({
        name: mockPatient.name,
        dob: mockPatient.dob,
        source: 'id_scan',
        confidence: 'medium',
        extractedFields: ['name', 'dob'],
        rawOCR: 'DRIVER LICENSE\nMARIA GARCIA\nDOB: 07/22/1992...',
      });
    })();
  }
  
  /**
   * Get patient by ID
   * @param {string} patientId - Patient ID
   * @returns {Promise<Object>} Patient data
   */
  async getPatient(patientId) {
    return this.wrapMethod('getPatient', async () => {
      const patient = cmrDataStore.get('patients', patientId);
      
      if (!patient) {
        return createMockResponse(null, 'Patient not found');
      }
      
      return createMockResponse(patient);
    })();
  }
  
  /**
   * Get all patients (for testing)
   * @returns {Promise<Object>} All patients
   */
  async getAllPatients() {
    return this.wrapMethod('getAllPatients', async () => {
      const patients = cmrDataStore.getAll('patients');
      return createMockResponse(patients);
    })();
  }
  
  /**
   * Save a discharge plan
   * @param {string} patientId - Patient ID
   * @param {Object} dischargePlan - Discharge plan data
   * @returns {Promise<Object>} Saved discharge plan
   */
  async saveDischargePlan(patientId, dischargePlan) {
    return this.wrapMethod('saveDischargePlan', async () => {
      const id = `discharge-${Date.now()}`;
      const plan = {
        id,
        patientId,
        createdAt: new Date(),
        ...dischargePlan,
      };
      
      cmrDataStore.set('dischargePlans', id, plan);
      
      // Also save to localStorage for persistence in dev mode
      if (typeof window !== 'undefined') {
        const plans = JSON.parse(localStorage.getItem('mockDischargePlans') || '[]');
        plans.push(plan);
        localStorage.setItem('mockDischargePlans', JSON.stringify(plans));
      }
      
      return createMockResponse(plan, 'Discharge plan saved');
    })();
  }
  
  /**
   * Get discharge plans for a patient
   * @param {string} patientId - Patient ID
   * @returns {Promise<Object>} Patient's discharge plans
   */
  async getDischargePlans(patientId) {
    return this.wrapMethod('getDischargePlans', async () => {
      const plans = cmrDataStore.query('dischargePlans', p => p.patientId === patientId);
      
      // Also check localStorage
      if (typeof window !== 'undefined') {
        const storedPlans = JSON.parse(localStorage.getItem('mockDischargePlans') || '[]');
        const additionalPlans = storedPlans.filter(p => p.patientId === patientId);
        plans.push(...additionalPlans);
      }
      
      return createMockResponse(plans);
    })();
  }
  
  /**
   * Generate a CMR access token (for QR code)
   * @param {string} patientId - Patient ID
   * @returns {Promise<Object>} Access token
   */
  async generateAccessToken(patientId) {
    return this.wrapMethod('generateAccessToken', async () => {
      const token = `cmr-token-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
      
      cmrDataStore.set('accessTokens', token, {
        patientId,
        token,
        createdAt: new Date(),
        expiresAt,
      });
      
      return createMockResponse({
        token,
        qrData: `cmr://${patientId}?token=${token}`,
        expiresAt,
      });
    })();
  }
  
  /**
   * Validate an access token
   * @param {string} token - Access token
   * @returns {Promise<Object>} Validation result
   */
  async validateToken(token) {
    return this.wrapMethod('validateToken', async () => {
      const tokenData = cmrDataStore.get('accessTokens', token);
      
      if (!tokenData) {
        return createMockResponse({ valid: false, reason: 'Token not found' });
      }
      
      if (new Date() > new Date(tokenData.expiresAt)) {
        return createMockResponse({ valid: false, reason: 'Token expired' });
      }
      
      return createMockResponse({
        valid: true,
        patientId: tokenData.patientId,
      });
    })();
  }
  
  /**
   * Add a patient to the mock store
   * @param {Object} patient - Patient data
   * @returns {Promise<Object>} Added patient
   */
  async addPatient(patient) {
    return this.wrapMethod('addPatient', async () => {
      const id = patient.id || `patient-${Date.now()}`;
      const newPatient = { ...patient, id };
      
      cmrDataStore.set('patients', id, newPatient);
      
      return createMockResponse(newPatient);
    })();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const mockCMRService = new MockCMRService();

// ============================================================================
// SECURE TRANSFER MOCK SERVICE
// ============================================================================

/**
 * SecureTransfer Mock Service
 * 
 * Simulates the patient-to-provider QR code token flow:
 * 1. Patient generates a time-limited token (like CMRQRCode.jsx)
 * 2. Token is "shared" via localStorage (simulating QR scan)
 * 3. Provider validates token and gets patient data (like CMRScanner.jsx)
 * 
 * This enables end-to-end testing of the CMR sharing workflow.
 */
class SecureTransferMockService extends MockServiceBase {
  constructor() {
    super('SecureTransferMockService');
    this.TOKEN_VALIDITY_MS = 5 * 60 * 1000; // 5 minutes
    this._loadTokens();
  }
  
  _loadTokens() {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('secureTransferTokens');
        this.tokens = stored ? JSON.parse(stored) : {};
      } catch {
        this.tokens = {};
      }
    } else {
      this.tokens = {};
    }
  }
  
  _saveTokens() {
    if (typeof window !== 'undefined') {
      localStorage.setItem('secureTransferTokens', JSON.stringify(this.tokens));
    }
  }
  
  /**
   * Generate a 7-digit manual entry code formatted as xxx-xxxx
   * @returns {string} 7-digit code
   */
  _generateManualCode() {
    // Generate 7 random digits
    const digits = Array.from({ length: 7 }, () => Math.floor(Math.random() * 10)).join('');
    return digits;
  }
  
  /**
   * Format a 7-digit code as xxx-xxxx
   * @param {string} code - 7 digit code
   * @returns {string} Formatted code
   */
  static formatManualCode(code) {
    if (!code || code.length !== 7) return code;
    return `${code.slice(0, 3)}-${code.slice(3)}`;
  }
  
  /**
   * Generate a mock CMR access token (patient side)
   * Simulates what CMRQRCode.jsx does
   * 
   * @param {string} patientId - Patient ID
   * @param {Object} options - Share settings
   * @returns {Promise<Object>} Token data with QR payload
   */
  async generateToken(patientId, options = { shareName: true, shareDob: true }) {
    return this.wrapMethod('generateToken', async () => {
      const patient = cmrDataStore.get('patients', patientId);
      
      if (!patient) {
        return createMockResponse(null, 'Patient not found');
      }
      
      const tokenId = `mock-token-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const expiresAt = Date.now() + this.TOKEN_VALIDITY_MS;
      
      // Generate a 7-digit manual entry code
      const manualCode = this._generateManualCode();
      
      // Create token data (what would be stored in msi3CmrAccessTokens)
      // Build allowedData array from options
      const allowedData = [];
      if (options.shareName) allowedData.push('name');
      if (options.shareDob) allowedData.push('dob');
      if (options.shareMedications) allowedData.push('medications');
      if (options.shareAllergies) allowedData.push('allergies');
      if (options.sharePmh) allowedData.push('pmh');
      if (options.sharePsh) allowedData.push('psh');
      if (options.shareOther) allowedData.push('other');
      
      const tokenData = {
        tokenId,
        patientUserId: patientId,
        patientName: options.shareName ? patient.name : null,
        patientDob: options.shareDob ? patient.dob : null,
        allowedData,
        expiresAt,
        used: false,
        createdAt: Date.now(),
        manualCode, // 7-digit code for manual entry
      };
      
      // Store token
      this.tokens[tokenId] = tokenData;
      this._saveTokens();
      
      // Create QR payload (what gets encoded in QR code)
      const qrPayload = {
        type: 'healthdesk_cmr_access',
        tokenId,
        patientId,
        expiresAt,
        v: 1,
      };
      
      const qrPayloadString = JSON.stringify(qrPayload);
      
      return createMockResponse({
        tokenId,
        qrPayload: qrPayloadString,
        qrData: qrPayloadString, // Alias for compatibility
        expiresAt,
        expiresIn: Math.floor(this.TOKEN_VALIDITY_MS / 1000),
        manualCode, // Raw 7-digit code
        manualCodeFormatted: SecureTransferMockService.formatManualCode(manualCode), // Formatted as xxx-xxxx
        patient: {
          name: tokenData.patientName,
          dob: tokenData.patientDob,
        },
      });
    })();
  }
  
  /**
   * Validate a manual entry code (provider side)
   * @param {string} code - 7-digit code (with or without dash)
   * @returns {Promise<Object>} Validation result with patient data
   */
  async validateManualCode(code) {
    return this.wrapMethod('validateManualCode', async () => {
      // Strip any non-digit characters (handles xxx-xxxx format)
      const cleanCode = code.replace(/\D/g, '');
      
      if (cleanCode.length !== 7) {
        return createMockResponse({ valid: false, error: 'Invalid code format. Please enter 7 digits.' });
      }
      
      // Find token with this manual code
      const tokenData = Object.values(this.tokens).find(t => t.manualCode === cleanCode);
      
      if (!tokenData) {
        return createMockResponse({ valid: false, error: 'Code not found. Please check and try again.' });
      }
      
      // Check expiration
      if (Date.now() > tokenData.expiresAt) {
        return createMockResponse({ valid: false, error: 'Code expired. Please ask patient for a new code.' });
      }
      
      // Check if already used
      if (tokenData.used) {
        return createMockResponse({ valid: false, error: 'Code already used. Please ask patient for a new code.' });
      }
      
      // Mark as used
      tokenData.used = true;
      tokenData.usedAt = Date.now();
      this._saveTokens();
      
      // Get full patient data
      const patient = cmrDataStore.get('patients', tokenData.patientUserId);
      
      return createMockResponse({
        valid: true,
        patientId: tokenData.patientUserId,
        patientData: {
          patientId: tokenData.patientUserId,
          name: tokenData.patientName,
          dob: tokenData.patientDob,
          allowedData: tokenData.allowedData,
          fullPatient: patient,
        },
      });
    })();
  }
  
  /**
   * Validate and consume a token (provider side)
   * Simulates what CMRScanner.jsx does
   * 
   * @param {string} qrData - QR code data (JSON string)
   * @returns {Promise<Object>} Validation result with patient data
   */
  async validateToken(qrData) {
    return this.wrapMethod('validateToken', async () => {
      
      let payload;
      try {
        payload = typeof qrData === 'string' ? JSON.parse(qrData) : qrData;
      } catch (err) {
        console.error('❌ [Mock] validateToken - Parse error:', err.message);
        return createMockResponse({ valid: false, error: 'Invalid QR format' });
      }
      
      // Validate payload structure
      if (payload.type !== 'healthdesk_cmr_access') {
        return createMockResponse({ valid: false, error: 'Invalid QR code type' });
      }
      
      const { tokenId, patientId } = payload;
      
      if (!tokenId || !patientId) {
        return createMockResponse({ valid: false, error: 'Missing required fields' });
      }
      
      // Get token data
      const tokenData = this.tokens[tokenId];
      
      if (!tokenData) {
        return createMockResponse({ valid: false, error: 'Token not found' });
      }
      
      // Check expiration
      if (Date.now() > tokenData.expiresAt) {
        return createMockResponse({ valid: false, error: 'Token expired' });
      }
      
      // Check if already used
      if (tokenData.used) {
        return createMockResponse({ valid: false, error: 'Token already used' });
      }
      
      // Mark as used
      tokenData.used = true;
      tokenData.usedAt = Date.now();
      this._saveTokens();
      
      // Get full patient data
      const patient = cmrDataStore.get('patients', patientId);
      
      return createMockResponse({
        valid: true,
        patientId,
        patientData: {
          patientId,
          name: tokenData.patientName,
          dob: tokenData.patientDob,
          allowedData: tokenData.allowedData,
          fullPatient: patient, // Include full patient data for testing
        },
      });
    })();
  }
  
  /**
   * Get current active token for a patient (for testing UI)
   * @param {string} patientId - Patient ID
   * @returns {Object|null} Active token or null
   */
  getActiveToken(patientId) {
    const now = Date.now();
    return Object.values(this.tokens).find(
      t => t.patientUserId === patientId && !t.used && t.expiresAt > now
    ) || null;
  }
  
  /**
   * Get all tokens (for debugging)
   * @returns {Object} All tokens
   */
  getAllTokens() {
    return { ...this.tokens };
  }
  
  /**
   * Clear all tokens
   */
  clearTokens() {
    this.tokens = {};
    this._saveTokens();
  }
  
  /**
   * Simulate QR scan by directly using a patient ID
   * This is a shortcut for testing without going through token generation
   * 
   * @param {string} patientId - Patient ID to link
   * @returns {Promise<Object>} Patient data
   */
  async quickLink(patientId) {
    return this.wrapMethod('quickLink', async () => {
      let patient = cmrDataStore.get('patients', patientId);
      
      if (!patient) {
        patient = {
          id: patientId,
          name: 'Test Patient',
          dob: '1990-01-01',
          allowedData: ['name', 'dob'],
        };
        cmrDataStore.set('patients', patientId, patient);
      }
      
      return createMockResponse({
        patientId,
        name: patient.name,
        dob: patient.dob,
        allowedData: patient.allowedData || ['name', 'dob'],
        source: 'mock_quick_link',
      });
    })();
  }
}

export const secureTransferMock = new SecureTransferMockService();

// ============================================================================
// BROWSER CONSOLE API
// ============================================================================

if (typeof window !== 'undefined') {
  window.__CMR_MOCKS__ = {
    service: mockCMRService,
    secureTransfer: secureTransferMock,
    patients: SAMPLE_PATIENTS,
    dischargePlans: SAMPLE_DISCHARGE_PLANS,
    store: cmrDataStore,
    
    // Helper functions
    getPatient: (id) => mockCMRService.getPatient(id),
    listPatients: () => mockCMRService.getAllPatients(),
    scanQR: (qr) => mockCMRService.getPatientByQR(qr),
    scanID: () => mockCMRService.extractPatientFromID('mock-base64'),
    
    // SecureTransfer helpers
    generateToken: (patientId, options) => secureTransferMock.generateToken(patientId, options),
    validateToken: (qrData) => secureTransferMock.validateToken(qrData),
    validateManualCode: (code) => secureTransferMock.validateManualCode(code),
    quickLink: (patientId) => secureTransferMock.quickLink(patientId),
    getActiveToken: (patientId) => secureTransferMock.getActiveToken(patientId),
    clearTokens: () => secureTransferMock.clearTokens(),
    formatManualCode: (code) => SecureTransferMockService.formatManualCode(code),
  };
  
}

const cmrMocksExports = {
  mockCMRService,
  secureTransferMock,
  SecureTransferMockService, // Export class for static method access
  SAMPLE_PATIENTS,
  SAMPLE_DISCHARGE_PLANS,
  cmrDataStore,
};

export default cmrMocksExports;

